import {
    Button,
    Card,
    CardContent,
    Checkbox,
    Chip,
    CircularProgress,
    Divider,
    FormControlLabel,
    IconButton,
    InputAdornment,
    MenuItem,
    Switch,
    TextField,
    Typography,
} from "@material-ui/core"
import Visibility from "@material-ui/icons/Visibility"
import VisibilityOff from "@material-ui/icons/VisibilityOff"
import React, { useEffect, useMemo, useState } from "react"
import {
    alterarPalavrasPdvoz,
    getPdvozConfig,
    listarCategoriasCardapio,
    mensagemDeErro,
    obterSegredosPdvoz,
    QualTemplate,
    salvarPdvozConfig,
    salvarSenhasPdvoz,
    salvarStoneCode,
    SegredosPdvoz,
    solicitarTemplatePdvoz,
    statusTemplatesPdvoz,
    testarTemplatePdvoz,
} from "../../services/pdvoz"
import { useSnackbar } from "../../utils/notificacoes"
import { MaquininhasDaLoja } from "./MaquininhasDaLoja"
import {
    aplicarPalavra,
    Campo,
    CAMPOS,
    CHAVE_PALAVRAS,
    novaPalavraDificil,
    PalavraDificil,
    defaultsDoSchema,
    montarConfigParaSalvar,
    OPCOES_PALAVRA,
    SECOES,
    selecaoWake,
    validarParametros,
} from "./parametros"

/**
 * Tela PDVoz: TODOS os parâmetros da loja que o app de voz (totem, garçom, cozinha,
 * registrador) usa — antes espalhados pelas Configurações de cada aparelho. A PWA os
 * busca no login, ao abrir e a cada 30 min, pelo mesmo GET das chaves de IA.
 *
 * O form é dirigido por schema (parametros.ts): somar um parâmetro é uma linha lá (e a
 * mesma chave no config-loja.js do PDVoz), não um bloco JSX aqui. Os três segredos da
 * loja (Stone Code, PIN do gerente e senha de estorno) têm endpoints próprios, porque
 * moram no ParametroGeral e não no JSON do PDVoz.
 */

const SEGREDOS_VAZIOS: SegredosPdvoz = { stoneCode: "", pinGerente: "", senhaEstorno: "" }

function validarSegredos(s: SegredosPdvoz): Record<string, string> {
    const erros: Record<string, string> = {}
    const stone = s.stoneCode.replace(/\s/g, "")
    if (stone && !/^\d{4,20}$/.test(stone)) erros.stoneCode = "só os dígitos da afiliação Stone"
    for (const k of ["pinGerente", "senhaEstorno"] as const) {
        const v = s[k].replace(/\s/g, "")
        if (v && !/^\d{4,8}$/.test(v)) erros[k] = "de 4 a 8 dígitos (só números)"
    }
    return erros
}

type StatusTpl = { txt: string; cor: "default" | "primary" | "secondary" }

function badgeStatus(status: string | undefined): StatusTpl {
    const st = String(status || "").toUpperCase()
    if (st === "APPROVED") return { txt: "Aprovado", cor: "primary" }
    if (st === "REJECTED") return { txt: "Rejeitado", cor: "secondary" }
    if (!st) return { txt: "Não criado", cor: "default" }
    return { txt: "Em análise", cor: "default" }
}

const TEMPLATES: { qual: QualTemplate; chave: string; titulo: string }[] = [
    { qual: "pix", chave: "whatsappPixTemplate", titulo: "só-PIX (1 botão)" },
    { qual: "pixCartao", chave: "whatsappPixCartaoTemplate", titulo: "PIX + cartão (2 botões)" },
    { qual: "comprovante", chave: "whatsappComprovanteTemplate", titulo: "comprovante (sem botão)" },
]

export function PDVozEditor({ idEstabelecimento }: { idEstabelecimento: number | string }) {
    const { enqueueSnackbar } = useSnackbar()
    const defaults = useMemo(() => defaultsDoSchema(), [])
    const [original, setOriginal] = useState<Record<string, any>>({})
    const [valores, setValores] = useState<Record<string, any>>(defaults)
    const [segredos, setSegredos] = useState<SegredosPdvoz>(SEGREDOS_VAZIOS)
    const [segredosSalvos, setSegredosSalvos] = useState<SegredosPdvoz>(SEGREDOS_VAZIOS)
    const [segredosDisponiveis, setSegredosDisponiveis] = useState(true)
    const [erros, setErros] = useState<Record<string, string>>({})
    const [revelar, setRevelar] = useState<Record<string, boolean>>({})
    const [carregando, setCarregando] = useState(true)
    const [salvando, setSalvando] = useState(false)

    // categorias faladas
    const [escolherCategorias, setEscolherCategorias] = useState(false)
    const [categoriasCardapio, setCategoriasCardapio] = useState<string[]>([])
    const [carregandoCategorias, setCarregandoCategorias] = useState(false)

    // palavras difíceis
    const [palavras, setPalavras] = useState<PalavraDificil[]>([])
    const [novoTermo, setNovoTermo] = useState("")
    const [novasVariantes, setNovasVariantes] = useState("")
    const [ocupadoPalavras, setOcupadoPalavras] = useState(false)

    // templates do WhatsApp
    const [statusTpl, setStatusTpl] = useState<Record<string, string> | null>(null)
    const [saidaTpl, setSaidaTpl] = useState("")
    const [ocupadoTpl, setOcupadoTpl] = useState(false)
    const [telTeste, setTelTeste] = useState("")
    const [qualTeste, setQualTeste] = useState<QualTemplate>("comprovante")

    useEffect(() => {
        let vivo = true
        setCarregando(true)
        Promise.all([
            getPdvozConfig(idEstabelecimento),
            obterSegredosPdvoz().catch(() => null),
        ])
            .then(([cfg, seg]) => {
                if (!vivo) return
                setOriginal(cfg || {})
                setPalavras(Array.isArray(cfg?.[CHAVE_PALAVRAS]) ? cfg[CHAVE_PALAVRAS] : [])
                const v = { ...defaults, ...(cfg || {}) }
                setValores(v)
                setEscolherCategorias(Array.isArray(v.producaoCategoriasFaladas) && v.producaoCategoriasFaladas.length > 0)
                setSegredosDisponiveis(!!seg)
                setSegredos(seg || SEGREDOS_VAZIOS)
                setSegredosSalvos(seg || SEGREDOS_VAZIOS)
            })
            .catch(e => enqueueSnackbar("Não consegui carregar a configuração: " + mensagemDeErro(e), { variant: "error" }))
            .finally(() => {
                if (vivo) setCarregando(false)
            })
        return () => {
            vivo = false
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [idEstabelecimento])

    const set = (k: string, v: any) => {
        setValores(prev => ({ ...prev, [k]: v }))
        if (erros[k]) setErros(prev => ({ ...prev, [k]: "" }))
    }
    const setVarios = (obj: Record<string, any>) => setValores(prev => ({ ...prev, ...obj }))

    const carregarCategorias = async () => {
        setCarregandoCategorias(true)
        try {
            const cats = await listarCategoriasCardapio(idEstabelecimento)
            setCategoriasCardapio(cats)
            if (!cats.length) enqueueSnackbar("Nenhuma categoria encontrada no cardápio.", { variant: "warning" })
        } catch (e) {
            enqueueSnackbar("Não consegui carregar as categorias: " + mensagemDeErro(e), { variant: "error" })
        } finally {
            setCarregandoCategorias(false)
        }
    }

    const salvar = async () => {
        const errosParam = validarParametros(valores)
        const errosSeg = segredosDisponiveis ? validarSegredos(segredos) : {}
        const todos = { ...errosParam, ...errosSeg }
        if (Object.keys(todos).length) {
            setErros(todos)
            const ROTULOS_SEGREDOS: Record<string, string> = {
                stoneCode: "Stone Code",
                pinGerente: "PIN do gerente",
                senhaEstorno: "Senha de estorno",
            }
            const rotulos = Object.keys(todos)
                .map(k => CAMPOS.find(c => c.key === k)?.label || ROTULOS_SEGREDOS[k] || k)
                .join(", ")
            enqueueSnackbar("Confira os campos: " + rotulos, { variant: "error" })
            return
        }
        setSalvando(true)
        try {
            const corpo = montarConfigParaSalvar(original, valores)
            await salvarPdvozConfig(corpo, idEstabelecimento)
            setOriginal(corpo)
        } catch (e) {
            enqueueSnackbar("Não consegui salvar a configuração: " + mensagemDeErro(e), { variant: "error" })
            setSalvando(false)
            return
        }
        // os segredos têm endpoint próprio: só manda o que mudou
        try {
            if (segredosDisponiveis) {
                const stone = segredos.stoneCode.replace(/\s/g, "")
                if (stone !== segredosSalvos.stoneCode) await salvarStoneCode(stone)
                const senhas: Record<string, string> = {}
                for (const k of ["pinGerente", "senhaEstorno"] as const) {
                    const v = segredos[k].replace(/\s/g, "")
                    if (v !== segredosSalvos[k]) senhas[k] = v
                }
                if (Object.keys(senhas).length) await salvarSenhasPdvoz(senhas)
                const novos = {
                    stoneCode: stone,
                    pinGerente: segredos.pinGerente.replace(/\s/g, ""),
                    senhaEstorno: segredos.senhaEstorno.replace(/\s/g, ""),
                }
                setSegredos(novos)
                setSegredosSalvos(novos)
            }
            enqueueSnackbar("Configuração do PDVoz salva. Os aparelhos recebem ao abrir o app (ou em até 30 min).", {
                variant: "success",
            })
        } catch (e) {
            enqueueSnackbar(
                "Os parâmetros foram salvos, mas o Stone Code / PIN / senha de estorno não: " + mensagemDeErro(e),
                { variant: "error" }
            )
        } finally {
            setSalvando(false)
        }
    }

    // ── palavras difíceis (endpoint próprio, gravam na hora) ─────────────────
    const alterarPalavras = async (corpo: Parameters<typeof alterarPalavrasPdvoz>[1], ok: string) => {
        setOcupadoPalavras(true)
        try {
            setPalavras(await alterarPalavrasPdvoz(idEstabelecimento, corpo))
            enqueueSnackbar(ok, { variant: "success" })
            return true
        } catch (e) {
            enqueueSnackbar("Não consegui salvar a palavra: " + mensagemDeErro(e), { variant: "error" })
            return false
        } finally {
            setOcupadoPalavras(false)
        }
    }

    const incluirPalavra = async () => {
        let p: PalavraDificil
        try {
            p = novaPalavraDificil(novoTermo, novasVariantes)
        } catch (e: any) {
            return enqueueSnackbar(e.message, { variant: "warning" })
        }
        const ja = palavras.some(x => x.chave === p.chave)
        if (await alterarPalavras({ operacao: "mesclar", palavras: [p] }, ja ? `"${p.termo}" atualizada` : `"${p.termo}" incluída`)) {
            setNovoTermo("")
            setNovasVariantes("")
        }
    }

    const tirarVariante = (p: PalavraDificil, v: string) =>
        alterarPalavras(
            {
                operacao: "substituir",
                palavras: [{ ...p, variantes: p.variantes.filter(x => x !== v), deTreino: (p.deTreino || []).filter(x => x !== v) }],
            },
            `"${v}" removido de ${p.termo}`
        )

    function renderPalavras() {
        return (
            <Card style={{ marginBottom: 16 }}>
                <CardContent>
                    <Typography variant="h6">Palavras difíceis</Typography>
                    <Divider style={{ margin: "8px 0 12px" }} />
                    <Typography variant="body2" color="textSecondary" style={{ marginBottom: 8 }}>
                        Palavras do cardápio que a transcrição de voz costuma errar e os jeitos como elas saem escritas — o
                        PDVoz troca pela palavra certa antes de montar o pedido. Vale para todos os aparelhos. O{" "}
                        <strong>treino por voz</strong> e a varredura do cardápio por IA continuam no app (Configurações →
                        Palavras difíceis e treino); o que um aparelho aprende chega nos outros. Aqui as mudanças gravam na
                        hora, sem o botão Salvar.
                    </Typography>
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                        <TextField
                            id="pdvoz-palavra-termo"
                            margin="dense"
                            variant="outlined"
                            label="Palavra do cardápio"
                            placeholder="ex: Yakisoba"
                            value={novoTermo}
                            onChange={e => setNovoTermo(e.target.value)}
                        />
                        <TextField
                            id="pdvoz-palavra-variantes"
                            margin="dense"
                            variant="outlined"
                            label="Como sai transcrita (separe por vírgula)"
                            placeholder="ex: iaquisoba, iaki soba"
                            value={novasVariantes}
                            onChange={e => setNovasVariantes(e.target.value)}
                            style={{ flex: 1, minWidth: 240 }}
                        />
                        <Button variant="outlined" disabled={ocupadoPalavras} onClick={incluirPalavra}>
                            Incluir
                        </Button>
                    </div>
                    {!palavras.length ? (
                        <Typography variant="body2" color="textSecondary" style={{ marginTop: 12 }}>
                            Nenhuma palavra ainda.
                        </Typography>
                    ) : (
                        <div style={{ marginTop: 12 }}>
                            {palavras.map(p => (
                                <div key={p.chave} style={{ padding: "8px 0", borderTop: "1px solid #eee" }}>
                                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                                        <strong>{p.termo}</strong>
                                        <Button
                                            size="small"
                                            disabled={ocupadoPalavras}
                                            onClick={() => alterarPalavras({ operacao: "remover", chaves: [p.chave] }, `"${p.termo}" removida`)}
                                        >
                                            Remover palavra
                                        </Button>
                                    </div>
                                    <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginTop: 4 }}>
                                        {p.variantes.length ? (
                                            p.variantes.map(v => (
                                                <Chip
                                                    key={v}
                                                    size="small"
                                                    label={(p.deTreino || []).includes(v) ? `🎙 ${v}` : v}
                                                    title={(p.deTreino || []).includes(v) ? "aprendido no treino por voz" : undefined}
                                                    onDelete={ocupadoPalavras ? undefined : () => tirarVariante(p, v)}
                                                />
                                            ))
                                        ) : (
                                            <Typography variant="caption" color="textSecondary">
                                                sem variante ainda — treine esta palavra no app
                                            </Typography>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </CardContent>
            </Card>
        )
    }

    // ── templates do WhatsApp ────────────────────────────────────────────────
    const verificarTemplates = async () => {
        const wabaId = String(valores.whatsappWabaId || "").trim()
        if (!wabaId) return setSaidaTpl("Informe o wabaId da conta WhatsApp da Cliente Fiel.")
        setOcupadoTpl(true)
        try {
            const lista = await statusTemplatesPdvoz(wabaId)
            const porNome: Record<string, string> = {}
            for (const t of lista) porNome[String(t.name)] = String(t.status || "")
            setStatusTpl(porNome)
            setSaidaTpl("")
        } catch (e) {
            setSaidaTpl("❌ " + mensagemDeErro(e))
        } finally {
            setOcupadoTpl(false)
        }
    }

    const solicitarTemplate = async (qual: QualTemplate) => {
        const wabaId = String(valores.whatsappWabaId || "").trim()
        if (!wabaId) return setSaidaTpl("Informe o wabaId da conta WhatsApp da Cliente Fiel.")
        setOcupadoTpl(true)
        setSaidaTpl("Solicitando à Meta…")
        try {
            const r = await solicitarTemplatePdvoz({
                wabaId,
                qual,
                nomePix: String(valores.whatsappPixTemplate || "").trim(),
                nomePixCartao: String(valores.whatsappPixCartaoTemplate || "").trim(),
                nomeComprovante: String(valores.whatsappComprovanteTemplate || "").trim(),
            })
            setSaidaTpl(
                "✅ Solicitado.\n" + (r?.detalhe || JSON.stringify(r)) +
                "\n\nAcompanhe no WhatsApp Manager (minutos a horas) e toque em \"Verificar status\"."
            )
        } catch (e) {
            setSaidaTpl("❌ " + mensagemDeErro(e))
        } finally {
            setOcupadoTpl(false)
        }
    }

    const testarEnvio = async () => {
        const tpl = TEMPLATES.find(t => t.qual === qualTeste)!
        const templateName = String(valores[tpl.chave] || "").trim()
        const phoneId = String(valores.whatsappOficialPhoneId || "").trim()
        if (!telTeste.trim()) return setSaidaTpl("Informe o WhatsApp de teste (DDD + número).")
        if (!phoneId) return setSaidaTpl("Informe o phoneId do número oficial.")
        setOcupadoTpl(true)
        setSaidaTpl(`Enviando "${templateName}" para ${telTeste}…`)
        try {
            const r = await testarTemplatePdvoz({ phoneId, templateName, telefone: telTeste, qual: qualTeste, idEstabelecimento })
            setSaidaTpl(
                "✅ POST aceito.\n" + JSON.stringify(r) +
                `\n\nSe NÃO chegar no WhatsApp, o template "${templateName}" provavelmente não está aprovado ` +
                "(a Meta aceita o POST mas não entrega template não aprovado)."
            )
        } catch (e) {
            setSaidaTpl("❌ " + mensagemDeErro(e) + "\n\nConfira o phoneId e se o template está APROVADO na Meta.")
        } finally {
            setOcupadoTpl(false)
        }
    }

    // ── renderização dos campos ──────────────────────────────────────────────
    const campoSenha = (key: string, label: string, valor: string, onChange: (v: string) => void, hint?: string, extra?: object) => (
        <TextField
            key={key}
            id={`pdvoz-${key}`}
            fullWidth
            margin="dense"
            variant="outlined"
            label={label}
            type={revelar[key] ? "text" : "password"}
            value={valor ?? ""}
            error={!!erros[key]}
            helperText={erros[key] || hint}
            onChange={e => onChange(e.target.value)}
            autoComplete="off"
            InputProps={{
                endAdornment: (
                    <InputAdornment position="end">
                        <IconButton
                            size="small"
                            aria-label={revelar[key] ? "ocultar" : "mostrar"}
                            onClick={() => setRevelar(prev => ({ ...prev, [key]: !prev[key] }))}
                        >
                            {revelar[key] ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                        </IconButton>
                    </InputAdornment>
                ),
            }}
            {...extra}
        />
    )

    function renderCategorias(c: Campo) {
        const selecionadas: string[] = Array.isArray(valores[c.key]) ? valores[c.key] : []
        const todasAsOpcoes = Array.from(new Set([...categoriasCardapio, ...selecionadas]))
        const alternar = (nome: string) =>
            set(c.key, selecionadas.includes(nome) ? selecionadas.filter(x => x !== nome) : [...selecionadas, nome])
        return (
            <div key={c.key}>
                <FormControlLabel
                    control={
                        <Switch
                            color="primary"
                            checked={!escolherCategorias}
                            onChange={e => {
                                const todas = e.target.checked
                                setEscolherCategorias(!todas)
                                if (todas) set(c.key, [])
                                else if (!categoriasCardapio.length) carregarCategorias()
                            }}
                        />
                    }
                    label="Falar todas as categorias"
                />
                <Typography variant="caption" display="block" color="textSecondary" style={{ marginLeft: 46, marginTop: -4 }}>
                    {c.hint} Com todas, categorias novas do cardápio já entram sozinhas.
                </Typography>
                {escolherCategorias && (
                    <div style={{ marginTop: 8 }}>
                        <Button size="small" variant="outlined" onClick={carregarCategorias} disabled={carregandoCategorias}>
                            {carregandoCategorias ? "Carregando…" : "Carregar categorias do cardápio"}
                        </Button>
                        <div style={{ display: "flex", flexWrap: "wrap", marginTop: 8 }}>
                            {todasAsOpcoes.map(nome => (
                                <FormControlLabel
                                    key={nome}
                                    style={{ minWidth: 220 }}
                                    control={
                                        <Checkbox color="primary" checked={selecionadas.includes(nome)} onChange={() => alternar(nome)} />
                                    }
                                    label={nome}
                                />
                            ))}
                        </div>
                        {!selecionadas.length && (
                            <Typography variant="caption" color="textSecondary">
                                Nenhuma marcada = a cozinha fala todas.
                            </Typography>
                        )}
                    </div>
                )}
            </div>
        )
    }

    function renderCampo(c: Campo) {
        if (c.visivel && !c.visivel(valores)) return null
        const valor = valores[c.key]
        const erro = erros[c.key]
        if (c.tipo === "categorias") return renderCategorias(c)
        if (c.tipo === "bool") {
            return (
                <div key={c.key} style={{ margin: "4px 0" }}>
                    <FormControlLabel
                        control={<Switch checked={!!valor} color="primary" onChange={e => set(c.key, e.target.checked)} />}
                        label={c.label}
                    />
                    {c.hint && (
                        <Typography variant="caption" display="block" color="textSecondary" style={{ marginLeft: 46, marginTop: -4 }}>
                            {c.hint}
                        </Typography>
                    )}
                </div>
            )
        }
        if (c.tipo === "palavra") {
            const sel = selecaoWake(valores)
            return (
                <React.Fragment key={c.key}>
                    <TextField
                        id={`pdvoz-${c.key}-selecao`}
                        select
                        fullWidth
                        margin="dense"
                        variant="outlined"
                        label={c.label}
                        value={sel}
                        helperText={c.hint}
                        onChange={e => setVarios(aplicarPalavra(valores, e.target.value))}
                    >
                        {OPCOES_PALAVRA.map(o => (
                            <MenuItem key={o.valor} value={o.valor}>
                                {o.rotulo}
                            </MenuItem>
                        ))}
                    </TextField>
                    {sel === "custom" && (
                        <TextField
                            id={`pdvoz-${c.key}`}
                            fullWidth
                            margin="dense"
                            variant="outlined"
                            label="Arquivo .ppn (URL/caminho)"
                            value={valor ?? ""}
                            error={!!erro}
                            helperText={erro || "Gerado no console da Picovoice (plataforma Web/WASM) e hospedado junto do app."}
                            onChange={e => set(c.key, e.target.value)}
                        />
                    )}
                </React.Fragment>
            )
        }
        if (c.tipo === "select") {
            // select numérico guarda number; o MenuItem trabalha com string
            const opcoes = c.opcoes || []
            const atual = valor === undefined || valor === null ? "" : String(valor)
            const lista = atual && !opcoes.some(o => o.valor === atual) ? [...opcoes, { valor: atual, rotulo: `${atual} (atual)` }] : opcoes
            return (
                <TextField
                    key={c.key}
                    id={`pdvoz-${c.key}`}
                    select
                    fullWidth
                    margin="dense"
                    variant="outlined"
                    label={c.label}
                    value={atual}
                    error={!!erro}
                    helperText={erro || c.hint}
                    onChange={e => set(c.key, c.inteiro ? Number(e.target.value) : e.target.value)}
                >
                    {lista.map(o => (
                        <MenuItem key={o.valor} value={o.valor}>
                            {o.rotulo}
                        </MenuItem>
                    ))}
                </TextField>
            )
        }
        if (c.tipo === "password") {
            return campoSenha(c.key, c.label, valor, v => set(c.key, v), c.hint)
        }
        if (c.tipo === "color") {
            const hex = String(valor ?? "")
            return (
                <div key={c.key} style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
                    <input
                        type="color"
                        aria-label={c.label}
                        value={/^#[0-9a-f]{6}$/i.test(hex) ? hex : "#ffffff"}
                        onChange={e => set(c.key, e.target.value)}
                        style={{ width: 48, height: 40, marginTop: 8, border: "none", background: "none", cursor: "pointer" }}
                    />
                    <TextField
                        id={`pdvoz-${c.key}`}
                        fullWidth
                        margin="dense"
                        variant="outlined"
                        label={c.label}
                        placeholder={c.corVazia ? "vazio = cor de fábrica" : "#ffffff"}
                        value={hex}
                        error={!!erro}
                        helperText={erro || c.hint}
                        onChange={e => set(c.key, e.target.value)}
                    />
                </div>
            )
        }
        return (
            <TextField
                key={c.key}
                id={`pdvoz-${c.key}`}
                fullWidth
                margin="dense"
                variant="outlined"
                label={c.label}
                type={c.tipo === "number" ? "number" : "text"}
                inputProps={c.tipo === "number" ? { min: c.min, max: c.max, step: c.step } : { maxLength: c.maxLen }}
                multiline={c.tipo === "textarea"}
                rows={c.tipo === "textarea" ? 3 : undefined}
                value={valor ?? ""}
                error={!!erro}
                helperText={erro || c.hint}
                onChange={e => set(c.key, e.target.value)}
            />
        )
    }

    function renderTemplates() {
        return (
            <div style={{ marginTop: 12 }}>
                <Divider style={{ margin: "8px 0 12px" }} />
                <Typography variant="subtitle2">Templates na Meta</Typography>
                <Typography variant="caption" color="textSecondary" display="block">
                    Enquanto os templates de PIX não estiverem aprovados, deixe o canal em "Central". "Solicitar
                    aprovação" cria só aquele template, com o nome digitado acima (salve antes, se mudou o nome).
                </Typography>
                {TEMPLATES.map(t => {
                    const nome = String(valores[t.chave] || "").trim()
                    const b = statusTpl ? badgeStatus(statusTpl[nome]) : null
                    return (
                        <div key={t.qual} style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 8, flexWrap: "wrap" }}>
                            <Typography variant="body2" style={{ minWidth: 200 }}>
                                {t.titulo}: <strong>{nome || "—"}</strong>
                            </Typography>
                            {b && <Chip size="small" color={b.cor} label={b.txt} />}
                            {b && b.txt !== "Aprovado" && (
                                <Button size="small" variant="outlined" disabled={ocupadoTpl || !nome} onClick={() => solicitarTemplate(t.qual)}>
                                    Solicitar aprovação
                                </Button>
                            )}
                        </div>
                    )
                })}
                <Button size="small" variant="outlined" style={{ marginTop: 8 }} disabled={ocupadoTpl} onClick={verificarTemplates}>
                    Verificar status
                </Button>
                <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 12, flexWrap: "wrap" }}>
                    <TextField
                        id="pdvoz-tpl-telefone"
                        margin="dense"
                        variant="outlined"
                        label="WhatsApp de teste (DDD + número)"
                        value={telTeste}
                        onChange={e => setTelTeste(e.target.value)}
                    />
                    <TextField
                        id="pdvoz-tpl-qual"
                        select
                        margin="dense"
                        variant="outlined"
                        label="Template"
                        value={qualTeste}
                        onChange={e => setQualTeste(e.target.value as QualTemplate)}
                        style={{ minWidth: 200 }}
                    >
                        {TEMPLATES.map(t => (
                            <MenuItem key={t.qual} value={t.qual}>
                                {t.titulo}
                            </MenuItem>
                        ))}
                    </TextField>
                    <Button variant="outlined" disabled={ocupadoTpl} onClick={testarEnvio}>
                        Testar envio
                    </Button>
                </div>
                {saidaTpl && (
                    <pre style={{ whiteSpace: "pre-wrap", background: "#f5f5f5", padding: 8, borderRadius: 4, fontSize: 12 }}>
                        {saidaTpl}
                    </pre>
                )}
            </div>
        )
    }

    if (carregando) {
        return (
            <div style={{ padding: 48, textAlign: "center" }}>
                <CircularProgress />
            </div>
        )
    }

    return (
        <div style={{ maxWidth: 860, margin: "0 auto", paddingBottom: 80 }}>
            <Typography variant="body2" color="textSecondary" style={{ margin: "8px 0 16px" }}>
                Estes parâmetros valem para <strong>todos os aparelhos PDVoz da loja</strong> (totem, garçom, cozinha,
                registrador) e chegam neles no login, ao abrir o app e a cada 30 minutos. O que é de cada aparelho —
                modo da tela, maquininha Stone, microfone, tamanho da fonte, tela invertida — continua nas
                Configurações do próprio app. A exceção é o modo da tela das maquininhas Stone, escolhido aqui
                para cada uma, em "Maquininhas da loja".
            </Typography>

            {SECOES.map(secao => (
                <React.Fragment key={secao}>
                    <Card style={{ marginBottom: 16 }}>
                        <CardContent>
                            <Typography variant="h6">{secao}</Typography>
                            <Divider style={{ margin: "8px 0 12px" }} />
                            {CAMPOS.filter(c => c.secao === secao).map(renderCampo)}
                            {secao === "Pagamento e WhatsApp" && renderTemplates()}
                        </CardContent>
                    </Card>

                    {secao === "Transcrição e fala" && renderPalavras()}

                    {secao === "Maquininha" && <MaquininhasDaLoja idEstabelecimento={idEstabelecimento} />}

                    {secao === "Chaves de IA" && (
                        <Card style={{ marginBottom: 16 }}>
                            <CardContent>
                                <Typography variant="h6">Stone e segredos da loja</Typography>
                                <Divider style={{ margin: "8px 0 12px" }} />
                                {!segredosDisponiveis ? (
                                    <Typography variant="body2" color="textSecondary">
                                        Não consegui ler o Stone Code, o PIN e a senha de estorno desta loja — recarregue a
                                        tela para editá-los.
                                    </Typography>
                                ) : (
                                    <>
                                        {campoSenha(
                                            "stoneCode",
                                            "Stone Code (afiliação Stone)",
                                            segredos.stoneCode,
                                            v => setSegredos(s => ({ ...s, stoneCode: v })),
                                            "A credencial que ativa as maquininhas Stone da loja. Sozinha ela não liga a cobrança pela Stone num aparelho que não é maquininha. Vazio = cada maquininha usa o que tiver digitado.",
                                            { inputProps: { inputMode: "numeric" } }
                                        )}
                                        {campoSenha(
                                            "pinGerente",
                                            "PIN do gerente",
                                            segredos.pinGerente,
                                            v => setSegredos(s => ({ ...s, pinGerente: v })),
                                            "Sai do modo totem (quiosque) e abre as Configurações dos aparelhos. De 4 a 8 dígitos. Vale em todos os aparelhos no próximo login/abertura.",
                                            { inputProps: { inputMode: "numeric", maxLength: 8 } }
                                        )}
                                        {campoSenha(
                                            "senhaEstorno",
                                            "Senha de estorno",
                                            segredos.senhaEstorno,
                                            v => setSegredos(s => ({ ...s, senhaEstorno: v })),
                                            "Exigida para estornar na maquininha um pagamento de cartão. De 4 a 8 dígitos. Sem senha (aqui e no aparelho), o estorno fica bloqueado.",
                                            { inputProps: { inputMode: "numeric", maxLength: 8 } }
                                        )}
                                    </>
                                )}
                            </CardContent>
                        </Card>
                    )}
                </React.Fragment>
            ))}

            <div
                style={{
                    position: "sticky",
                    bottom: 0,
                    background: "#eceff1",
                    padding: "12px 0",
                    textAlign: "right",
                }}
            >
                <Button variant="contained" color="primary" onClick={salvar} disabled={salvando}>
                    {salvando ? "Salvando…" : "Salvar configuração"}
                </Button>
            </div>
        </div>
    )
}
